import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  CircularProgress
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RefreshIcon from '@mui/icons-material/Refresh';
import LogoutIcon from '@mui/icons-material/Logout';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import './SessionTimeoutWarning.css';

// Show warning 5 minutes (300 seconds) before expiration
const WARNING_THRESHOLD_SECONDS = 5 * 60;

const SessionTimeoutWarning: React.FC = () => {
  const { t } = useTranslation();
  const { tokenExpiration, refreshToken, logout, isAuthenticated } = useAuth();
  
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(WARNING_THRESHOLD_SECONDS);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  const calculateSecondsRemaining = useCallback(() => {
    if (!tokenExpiration) return null;
    const now = Date.now();
    const remaining = Math.floor((tokenExpiration - now) / 1000);
    return remaining;
  }, [tokenExpiration]);

  // Check for approaching expiration
  useEffect(() => {
    if (!isAuthenticated || !tokenExpiration) {
      setShowWarning(false);
      return;
    }

    const checkExpiration = () => {
      const remaining = calculateSecondsRemaining();
      
      if (remaining === null) return;

      // If already expired, logout
      if (remaining <= 0) {
        setShowWarning(false);
        logout();
        return;
      }

      // Show warning if within threshold
      if (remaining <= WARNING_THRESHOLD_SECONDS) {
        setShowWarning(true);
        setSecondsRemaining(remaining);
      } else {
        setShowWarning(false);
      }
    };

    // Check immediately
    checkExpiration();

    // Then check every second
    const interval = setInterval(checkExpiration, 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated, tokenExpiration, calculateSecondsRemaining, logout]);

  // Countdown timer when warning is shown
  useEffect(() => {
    if (!showWarning) return;

    const countdown = setInterval(() => {
      setSecondsRemaining(prev => {
        if (prev <= 1) {
          clearInterval(countdown);
          logout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdown);
  }, [showWarning, logout]);

  const handleExtendSession = async () => {
    setIsRefreshing(true);
    setRefreshError(null);
    
    try {
      await refreshToken();
      setShowWarning(false);
    } catch (error) {
      console.error('Failed to refresh token:', error);
      setRefreshError(t('sessionTimeout.refreshError'));
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLogout = () => {
    setShowWarning(false);
    logout();
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progressValue = (secondsRemaining / WARNING_THRESHOLD_SECONDS) * 100;

  if (!showWarning) {
    return null;
  }

  return (
    <Dialog
      open={showWarning}
      onClose={() => {}} // Prevent closing by clicking outside
      maxWidth="sm"
      fullWidth
      className="session-timeout-dialog"
      aria-labelledby="session-timeout-title"
      aria-describedby="session-timeout-description"
    >
      <DialogTitle id="session-timeout-title" className="session-timeout-title">
        <Box display="flex" alignItems="center" gap={1}>
          <AccessTimeIcon color="warning" />
          <Typography variant="h6" component="span">
            {t('sessionTimeout.title')}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent className="session-timeout-content">
        <Box className="countdown-container">
          <Box className="countdown-circle">
            <CircularProgress
              variant="determinate"
              value={progressValue}
              size={120}
              thickness={4}
              className={`countdown-progress ${secondsRemaining <= 60 ? 'critical' : ''}`}
              aria-label={t('sessionTimeout.timeRemaining', { time: formatTime(secondsRemaining) })}
            />
            <Typography variant="h4" className="countdown-time" aria-live="polite">
              {formatTime(secondsRemaining)}
            </Typography>
          </Box>
        </Box>
        
        <Typography 
          id="session-timeout-description"
          variant="body1" 
          align="center" 
          className="timeout-message"
        >
          {t('sessionTimeout.message')}
        </Typography>
        
        <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 1 }}>
          {t('sessionTimeout.action')}
        </Typography>
        
        {refreshError && (
          <Typography 
            variant="body2" 
            color="error" 
            align="center" 
            sx={{ mt: 2 }}
            role="alert"
          >
            {refreshError}
          </Typography>
        )}
        
        <LinearProgress 
          variant="determinate" 
          value={progressValue} 
          className={`timeout-progress ${secondsRemaining <= 60 ? 'critical' : ''}`}
          sx={{ mt: 3, mb: 1 }}
          aria-hidden="true"
        />
      </DialogContent>
      
      <DialogActions className="session-timeout-actions">
        <Button
          onClick={handleLogout}
          color="inherit"
          startIcon={<LogoutIcon />}
          disabled={isRefreshing}
          aria-label={t('sessionTimeout.logout')}
        >
          {t('sessionTimeout.logout')}
        </Button>
        <Button
          onClick={handleExtendSession}
          variant="contained"
          color="primary"
          startIcon={isRefreshing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
          disabled={isRefreshing}
          aria-label={t('sessionTimeout.extendSession')}
        >
          {isRefreshing ? t('sessionTimeout.extending') : t('sessionTimeout.extendSession')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionTimeoutWarning;

